<template>
  <div>
    <field-select-module v-model="form.stdInfo" :board="this.board" :is-print="isPrint" :readonly="board.readonly" :types="['writer_name', 'date']" />
    <div>
      <h5 class="my-4"><i class="dot"></i>{{ $t("공지사항") }}</h5>

      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <div>{{ $t("구분") }}</div>
          <div>
            <vs-select class="select-alt-type" autocomplete v-model="form.alertType" :disabled="board.readonly">
              <vs-select-item v-for="item in ['긴급', '안전', '안내']" :key="item" :value="item" :text="$t(item)" />
            </vs-select>
          </div>
        </div>
        <div class="writeGridLineItem">
          <div>{{ $t("공지내용") }}</div>
          <div>
            <ml-textarea v-model="form.content" :readonly="board.readonly" />
          </div>
        </div>
        <div v-if="!board.readonly && !isPrint" class="writeGridLineItem">
          <div>{{ $t("알림 발송수단") }}</div>
          <div class="h-full flex items-center">
            <radio-group
              v-model="form.발송수단"
              :items="[
                { label: $t('발송하지 않음'), value: 'NONE' },
                { label: $t('문자(SMS)'), value: 'SMS' },
              ]"
            />
          </div>
        </div>
        <div v-if="form.발송수단 === 'SMS'" class="writeGridLineItem">
          <div>{{ $t("제한된 메시지") }}</div>
          <div class="relative">
            <textarea
              ref="autoResizeTextarea"
              v-model="form.limitedMessage"
              @input="handleLimitedInput"
              class="w-full border border-gray-300 rounded p-2 resize-none"
              style="text-align: left; min-height: 72px; overflow-y: hidden"
              rows="3"
              :placeholder="$t('한글 45자 / 영어 90자까지 입력 가능')"
            />
            <div class="absolute bottom-2 right-2 text-xs text-gray-500 bg-white px-1">{{ getCurrentBytes() }}/90 bytes</div>
          </div>
        </div>
      </div>
      <file-upload-module v-if="!isPrint && form.fileList.length > 0" v-model="form.fileList" class="w-full" :isPrint="isPrint" :readonly="board.readonly" />
    </div>
  </div>
</template>

<script>
import RadioGroup from "@/components/radio/RadioGroup.vue";
import FileUploadModule from "@/views/modules/FileUploadModule.vue";

export default {
  components: {
    RadioGroup,
    FileUploadModule,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
    header: Object,
  },
  async created() {},
  mounted() {
    // 컴포넌트 마운트 후 textarea 초기 높이 설정
    this.$nextTick(() => {
      if (this.$refs.autoResizeTextarea) {
        this.autoResizeTextarea(this.$refs.autoResizeTextarea);
      }
    });
  },
  data() {
    return {
      form: {
        stdInfo: {},
        alertType: "", // 긴급, 안전, 안내
        발송수단: "NONE",
        fileList: [],
        limitedMessage: "", // EUC-KR 90bytes 제한 메시지
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
    "form.limitedMessage": {
      handler(newValue) {
        // 바이트 제한 확인 및 적용
        const truncatedValue = this.truncateToByteLimit(newValue, 90);
        if (newValue !== truncatedValue) {
          this.form.limitedMessage = truncatedValue;
        }

        // textarea 높이 자동 조정
        this.$nextTick(() => {
          this.autoResizeTextarea(this.$refs.autoResizeTextarea);
        });
      },
    },
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
  },
  methods: {
    translationMap() {
      const prefix = "공지사항";

      const translateData = {
        [`${prefix}.기본정보.작성자이름`]: this.form.stdInfo.writer_name,
        [`${prefix}.기본정보.작성일`]: this.form.stdInfo.date,
        [`${prefix}.구분`]: this.form.alertType,
        [`${prefix}.공지내용`]: this.form.content,
      };

      return translateData;
    },

    translate(translate) {
      const prefix = "공지사항";

      this.form.stdInfo.writer_name = translate[`${prefix}.기본정보.작성자이름`];
      this.form.stdInfo.date = translate[`${prefix}.기본정보.작성일`];
      this.form.alertType = translate[`${prefix}.구분`];
      this.form.content = translate[`${prefix}.공지내용`];
    },

    // EUC-KR 인코딩으로 바이트 수 계산
    getEucKrByteLength(str) {
      if (!str) return 0;
      let byteLength = 0;
      for (let i = 0; i < str.length; i++) {
        const charCode = str.charCodeAt(i);
        // 한글 및 기타 멀티바이트 문자는 2바이트
        if (charCode > 127) {
          byteLength += 2;
        } else {
          // ASCII 문자는 1바이트
          byteLength += 1;
        }
      }
      return byteLength;
    },

    // 현재 입력된 바이트 수 반환
    getCurrentBytes() {
      return this.getEucKrByteLength(this.form.limitedMessage);
    },

    // EUC-KR 기준으로 90바이트까지만 입력 허용
    truncateToByteLimit(str, maxBytes) {
      if (!str) return "";
      let byteLength = 0;
      let result = "";

      for (let i = 0; i < str.length; i++) {
        const char = str[i];
        const charCode = char.charCodeAt(0);
        const charByteLength = charCode > 127 ? 2 : 1;

        if (byteLength + charByteLength <= maxBytes) {
          result += char;
          byteLength += charByteLength;
        } else {
          break;
        }
      }
      return result;
    },

    // textarea 자동 높이 조정
    autoResizeTextarea(textarea) {
      if (!textarea) return;

      // 높이를 초기화하여 정확한 scrollHeight 계산
      textarea.style.height = "auto";

      // 최소 높이 설정 (3줄 기준)
      const minHeight = 72; // rows="3"의 기본 높이
      const maxHeight = 200; // 최대 높이 제한

      // 내용에 맞는 높이 계산
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

      textarea.style.height = newHeight + "px";

      // 최대 높이에 도달하면 스크롤 활성화
      if (scrollHeight > maxHeight) {
        textarea.style.overflowY = "auto";
      } else {
        textarea.style.overflowY = "hidden";
      }
    },

    // 입력 제한 처리
    handleLimitedInput(event) {
      const inputValue = event.target.value;
      const truncatedValue = this.truncateToByteLimit(inputValue, 90);

      // 항상 v-model 값을 업데이트하여 바이트 카운터가 정확히 반영되도록 함
      this.form.limitedMessage = truncatedValue;

      // textarea 높이 자동 조정
      this.$nextTick(() => {
        this.autoResizeTextarea(this.$refs.autoResizeTextarea);
      });

      if (inputValue !== truncatedValue) {
        this.$nextTick(() => {
          // 커서 위치를 끝으로 이동
          const textarea = event.target;
          textarea.value = truncatedValue;
          textarea.setSelectionRange(truncatedValue.length, truncatedValue.length);

          // 높이 재조정
          this.autoResizeTextarea(textarea);
        });
      }
    },
  },
};
</script>
